import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Ban,
  BellRing,
  CalendarClock,
  CheckCheck,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Loader2,
  MessageCircle,
  PackageCheck,
  RefreshCw,
  Search,
  Store,
  XCircle,
} from 'lucide-react';
import DateSelector from '../control-tower/DateSelector';
import DeliveryActionModal from './DeliveryActionModal';
import {
  apiErrorMessage,
  fetchStoreSignals,
  reviewStoreSignal,
} from './operationalIntelligenceApi';

const SIGNAL_LABELS = {
  recipient_confirmation: 'Cliente confirmó a la tienda',
  recipient_response: 'Cliente respondió a la tienda',
  cancellation_request: 'Solicitud de cancelación',
  postponement_request: 'Solicitud de postergación',
};

const OUTCOME_LABELS = {
  confirmed_reception: 'Recepción confirmada',
  negative_response: 'Respuesta negativa / con observaciones',
  cancellation_requested: 'Cliente solicita cancelar',
  postponement_requested: 'Cliente solicita postergar',
  unclear: 'Respuesta poco clara',
};

const CHANNEL_LABELS = {
  store_whatsapp: 'WhatsApp de la tienda',
  phone: 'Llamada telefónica',
  other: 'Otro canal',
};

const REASON_LABELS = {
  customer_requested: 'Cliente solicitó cancelación',
  no_response: 'No responde',
  store_requested: 'Solicitud de la tienda',
  duplicate_order: 'Pedido duplicado',
  invalid_address: 'Dirección inválida',
  other: 'Otro',
};

const STATE_LABELS = {
  REPORTED: 'Sin reconocer',
  ACKNOWLEDGED: 'Reconocida',
  RESOLVED: 'Resuelta',
  REJECTED: 'No aplicada',
};

const STATE_STYLES = {
  REPORTED: 'border-violet-200 bg-violet-50 text-violet-700',
  ACKNOWLEDGED: 'border-blue-200 bg-blue-50 text-blue-700',
  RESOLVED: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  REJECTED: 'border-slate-300 bg-slate-100 text-slate-700',
};

const STAGE_LABELS = {
  NO_PACKAGE: 'Sin paquete',
  IN_WAREHOUSE: 'En almacén',
  IN_FASTMAX: 'En Fast Max',
  WITH_DRIVER: 'Con conductor',
  OUT_FOR_DELIVERY: 'En ruta',
  POSTPONED_WITH_DRIVER: 'Postergada con conductor',
  UNKNOWN: 'Etapa por revisar',
};

function todayInLima() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Lima', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function formatWhen(value) {
  if (!value) return 'Sin fecha';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('es-PE', {
    timeZone: 'America/Lima', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function SignalIcon({ type, className = 'h-5 w-5' }) {
  if (type === 'cancellation_request') return <Ban className={className} />;
  if (type === 'postponement_request') return <CalendarClock className={className} />;
  if (type === 'recipient_confirmation') return <CheckCircle2 className={className} />;
  return <MessageCircle className={className} />;
}

export default function StoreSignalsInbox({ initialDate, refreshSignal, onOpenContext, onToast }) {
  const [date, setDate] = useState(initialDate || todayInLima);
  const [showCalendar, setShowCalendar] = useState(false);
  const [state, setState] = useState('OPEN');
  const [signalType, setSignalType] = useState('');
  const [userId, setUserId] = useState('');
  const [search, setSearch] = useState('');
  const [payload, setPayload] = useState({ summary: {}, accounts: [], items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busySignalId, setBusySignalId] = useState(null);
  const [deliveryAction, setDeliveryAction] = useState(null);

  useEffect(() => {
    if (initialDate) setDate(initialDate);
  }, [initialDate]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      setPayload(await fetchStoreSignals({
        date,
        state,
        signal_type: signalType || undefined,
        user_id: userId || undefined,
      }));
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'No se pudo cargar la bandeja de señales'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [date, signalType, state, userId]);

  useEffect(() => { load(); }, [load, refreshSignal]);
  useEffect(() => {
    const timer = window.setInterval(() => load({ quiet: true }), 60000);
    return () => window.clearInterval(timer);
  }, [load]);

  const items = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return payload.items || [];
    return (payload.items || []).filter(item => (
      item.delivery?.recipient_name?.toLowerCase().includes(term)
      || item.delivery?.recipient_phone?.includes(term)
      || item.delivery?.seller_username?.toLowerCase().includes(term)
      || String(item.delivery_point_id).includes(term)
    ));
  }, [payload.items, search]);

  async function review(item, decision) {
    const question = decision === 'rejected'
      ? '¿Marcar este reporte como no aplicado? La entrega no cambiará de estado.'
      : null;
    if (question && !window.confirm(question)) return;
    setBusySignalId(item.signal_id);
    try {
      await reviewStoreSignal(item.signal_id, { decision });
      onToast?.(
        decision === 'acknowledged'
          ? 'Señal reconocida. La tienda podrá ver que operaciones la revisó.'
          : decision === 'rejected'
            ? 'Reporte cerrado sin cambiar la entrega.'
            : 'Señal resuelta. La entrega continúa sin cambio.'
      );
      await load({ quiet: true });
    } catch (requestError) {
      setError(apiErrorMessage(requestError));
      if (requestError?.response?.status === 409) await load({ quiet: true });
    } finally {
      setBusySignalId(null);
    }
  }

  const summary = payload.summary || {};
  const cards = [
    { label: 'Pendientes', value: summary.open_total || 0, icon: BellRing, tone: 'border-violet-200 bg-violet-50 text-violet-700' },
    { label: 'Sin reconocer', value: summary.unacknowledged || 0, icon: Clock3, tone: 'border-blue-200 bg-blue-50 text-blue-700' },
    { label: 'Cancelar', value: summary.cancellation_requests || 0, icon: Ban, tone: 'border-red-200 bg-red-50 text-red-700' },
    { label: 'Postergar', value: summary.postponement_requests || 0, icon: CalendarClock, tone: 'border-amber-200 bg-amber-50 text-amber-700' },
    { label: 'Con conductor', value: summary.with_driver_open || 0, icon: PackageCheck, tone: 'border-pink-200 bg-pink-50 text-brand-primary' },
  ];

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <div>
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-primary"><BellRing className="h-4 w-4" /> Señales de tienda</p>
            <h2 className="mt-1 text-xl font-black text-slate-900 sm:text-2xl">Bandeja operativa</h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-500">Confirmaciones, respuestas y solicitudes reportadas por cada cuenta. Una señal no modifica la entrega por sí sola.</p>
          </div>
          <button type="button" onClick={() => load()} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </button>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {cards.map(card => {
          const Icon = card.icon;
          return <div key={card.label} className={`rounded-2xl border p-4 ${card.tone}`}><div className="flex items-center justify-between gap-2"><p className="text-xs font-extrabold">{card.label}</p><Icon className="h-4 w-4" /></div><p className="mt-2 text-2xl font-black">{card.value}</p></div>;
        })}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[230px_180px_220px_220px_1fr] xl:items-end">
          <DateSelector selectedDate={date} onDateChange={setDate} showCalendar={showCalendar} onToggleCalendar={() => setShowCalendar(value => !value)} label="Fecha operativa" />
          <label className="block text-xs font-extrabold uppercase tracking-wide text-slate-500">Estado
            <select value={state} onChange={event => setState(event.target.value)} className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-brand-primary">
              <option value="OPEN">Pendientes</option><option value="RESOLVED">Resueltas</option><option value="REJECTED">No aplicadas</option><option value="ALL">Todas</option>
            </select>
          </label>
          <label className="block text-xs font-extrabold uppercase tracking-wide text-slate-500">Tipo
            <select value={signalType} onChange={event => setSignalType(event.target.value)} className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-brand-primary">
              <option value="">Todos</option><option value="recipient_confirmation">Confirmación</option><option value="recipient_response">Respuesta</option><option value="cancellation_request">Cancelar</option><option value="postponement_request">Postergar</option>
            </select>
          </label>
          <label className="block text-xs font-extrabold uppercase tracking-wide text-slate-500">Cuenta
            <select value={userId} onChange={event => setUserId(event.target.value)} className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-brand-primary">
              <option value="">Todas</option>{(payload.accounts || []).map(account => <option key={account.user_id} value={account.user_id}>{account.username}</option>)}
            </select>
          </label>
          <label className="block text-xs font-extrabold uppercase tracking-wide text-slate-500">Buscar
            <span className="relative mt-2 block"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Destinatario, teléfono, tienda o ID" className="w-full rounded-xl border border-slate-300 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand-primary" /></span>
          </label>
        </div>
      </section>

      {error && <div className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{error}</div>}

      {loading && !payload.items?.length ? (
        <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center text-slate-500"><Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-brand-primary" />Cargando señales...</div>
      ) : items.length ? (
        <div className="grid gap-4 xl:grid-cols-2">
          {items.map(item => {
            const delivery = item.delivery || {};
            const isOpen = ['REPORTED', 'ACKNOWLEDGED'].includes(item.state);
            const requestAction = item.signal_type === 'cancellation_request' ? 'cancel' : item.signal_type === 'postponement_request' ? 'postpone' : null;
            return (
              <article key={item.signal_id} className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${item.urgency === 'CRITICAL' ? 'border-red-300' : 'border-slate-200'}`}>
                <div className={`h-1 ${item.urgency === 'CRITICAL' ? 'bg-red-600' : item.urgency === 'HIGH' ? 'bg-amber-500' : 'bg-violet-500'}`} />
                <div className="p-4 sm:p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-extrabold uppercase text-slate-700"><SignalIcon type={item.signal_type} className="h-3.5 w-3.5" />{SIGNAL_LABELS[item.signal_type] || item.signal_type}</span>
                    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-extrabold uppercase ${STATE_STYLES[item.state] || STATE_STYLES.REPORTED}`}>{STATE_LABELS[item.state] || item.state}</span>
                  </div>

                  <div className="mt-4 flex flex-col justify-between gap-3 sm:flex-row">
                    <div className="min-w-0">
                      <p className="text-xs font-extrabold text-brand-primary">#{delivery.id} · {delivery.seller_username}</p>
                      <h3 className="mt-1 truncate text-lg font-black text-slate-900">{delivery.recipient_name || 'Sin nombre'}</h3>
                      <p className="text-sm text-slate-600">{delivery.recipient_phone || 'Sin teléfono'}{delivery.district ? ` · ${delivery.district}` : ''}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600 sm:text-right">
                      <p className="font-extrabold text-slate-800">{STAGE_LABELS[delivery.package_stage] || delivery.package_stage}</p>
                      <p className="mt-1">Reportado {formatWhen(item.reported_at)}</p>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-2 text-xs sm:grid-cols-2">
                    {item.outcome && <p className="rounded-xl border border-slate-200 bg-slate-50 p-3"><strong>Resultado:</strong> {OUTCOME_LABELS[item.outcome] || item.outcome}</p>}
                    {item.requested_date && <p className="rounded-xl border border-amber-200 bg-amber-50 p-3"><strong>Fecha solicitada:</strong> {item.requested_date}</p>}
                    {item.reason_code && <p className="rounded-xl border border-slate-200 bg-slate-50 p-3"><strong>Motivo:</strong> {REASON_LABELS[item.reason_code] || item.reason_code}</p>}
                    {item.channel && <p className="rounded-xl border border-slate-200 bg-slate-50 p-3"><strong>Canal:</strong> {CHANNEL_LABELS[item.channel] || item.channel}</p>}
                  </div>
                  {item.note && <p className="mt-3 rounded-xl border border-violet-100 bg-violet-50 p-3 text-xs leading-5 text-violet-900">“{item.note}”</p>}

                  <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
                    <button type="button" onClick={() => onOpenContext?.(item)} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50"><ExternalLink className="h-4 w-4" />Abrir contexto</button>
                    {isOpen && item.state === 'REPORTED' && <button type="button" disabled={busySignalId === item.signal_id} onClick={() => review(item, 'acknowledged')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 py-2.5 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-60"><CheckCheck className="h-4 w-4" />Reconocer</button>}
                    {isOpen && requestAction && <button type="button" onClick={() => setDeliveryAction({ item, action: requestAction })} className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-xs font-bold text-white ${requestAction === 'cancel' ? 'bg-red-600 hover:bg-red-700' : 'bg-brand-primary hover:bg-brand-primary/90'}`}>{requestAction === 'cancel' ? <Ban className="h-4 w-4" /> : <CalendarClock className="h-4 w-4" />}Aplicar</button>}
                    {isOpen && !requestAction && <button type="button" disabled={busySignalId === item.signal_id} onClick={() => review(item, 'continue')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 py-2.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-60"><CheckCircle2 className="h-4 w-4" />Continuar</button>}
                    {isOpen && <button type="button" disabled={busySignalId === item.signal_id} onClick={() => review(item, 'rejected')} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 px-3 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-60"><XCircle className="h-4 w-4" />No aplicar</button>}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center"><Store className="mx-auto h-11 w-11 text-slate-300" /><h3 className="mt-3 text-lg font-black text-slate-800">Sin señales para este filtro</h3><p className="mt-1 text-sm text-slate-500">Las nuevas confirmaciones y solicitudes aparecerán aquí.</p></div>
      )}

      {deliveryAction && (
        <DeliveryActionModal
          delivery={deliveryAction.item.delivery}
          action={deliveryAction.action}
          sourceStoreSignal={deliveryAction.item}
          onClose={() => setDeliveryAction(null)}
          onCompleted={async () => {
            setDeliveryAction(null);
            onToast?.(deliveryAction.action === 'cancel' ? 'Entrega cancelada y señal resuelta.' : 'Entrega postergada y señal resuelta.');
            await load({ quiet: true });
          }}
          onStateChanged={() => load({ quiet: true })}
        />
      )}
    </div>
  );
}
